import { AdTemplate } from '@/components/AdTemplate';
import { AuthorDetails } from '@/components/AuthorDetails';
import { Base } from '@/components/Base';
import { Consent } from '@/components/Consent';
import { Pagination } from '@/components/Pagination';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { Search } from '@/components/Search';
import { SoMeProps } from '@/components/SoMe';
import { Tracking } from '@/components/Tracking';
import { BylineData } from '@/types/bylineData';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { getUrl } from '@/utils/getUrl';
import { withSerializationGuard } from '@/utils/withSerializationGuard';
import { getLabradorConfig } from 'lib/configs/labrador';
import { loadAdsData } from 'lib/data';
import { ConsolidatedAuthor, fetchAuthorArticles, getAuthorBySlug } from 'lib/labrador/author-page';
import { AdsMeta } from 'lib/labrador/types';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import { isTruthy } from 'typesafe-utils';

interface PageAuthorsProps {
  author: ConsolidatedAuthor;
  searchResults: BylineData;
  adsData: AdsMeta;
  titleSuffix: string;
}

const PageAuthors: NextPage<PageAuthorsProps> = ({ author, searchResults, adsData, titleSuffix }) => {
  const src = author?.imageBig?.replace('%3F', '?');
  const authorName = `${author.firstName} ${author.lastName}`;
  const brandName = getCurrentBrand().name;
  const publishedUrl = `/vara-skribenter/${author.slug}`;
  const canonical = getUrl(publishedUrl)?.href;
  const { totalItems = 0, limit = 0, start = 0 } = searchResults || {};

  const soMeList = [
    { name: 'facebook', href: author.facebook },
    { name: 'x', href: author.twitter },
    { name: 'instagram', href: author.instagram },
  ].filter(({ href }) => isTruthy(href)) as SoMeProps[];

  const description = `Artiklar skrivna av ${authorName}. Läs alla artiklar av ${authorName} på ${brandName}.`;
  return (
    <Base
      content={
        <>
          <Head>
            <title>{`${authorName} ${titleSuffix}`}</title>
            <meta name="title" content={`${authorName} ${titleSuffix}`} key="metaTitle" />
            <meta name="description" content={description} key="metaDescription" />
            <link rel="canonical" href={canonical} />
          </Head>
          <Consent />
          <AdTemplate adsData={adsData} />
          <Tracking />
          <SchemaMarkup
            pageData={{
              type: 'front',
              meta: {},
              data: {
                publishedUrl,
                seoTitle: authorName,
                seoDescription: description,
              },
              children: [],
            }}
          />

          {start === 0 && !author.isRetired && (
            <AuthorDetails
              title={authorName}
              image={{ src, alt: authorName }}
              description={author.description}
              soMeList={soMeList}
              caption={<>{author.publicEmail && <a href={`mailto:${author.publicEmail}`}>{author.publicEmail}</a>}</>}
            />
          )}

          <Search
            hideButton={true}
            query={`${authorName}`}
            total={totalItems}
            adsUniqueId={adsData?.adsUniqueId}
            initial={{ next: searchResults?.nextPage, results: searchResults?.items }}
            page={start + 1}
            pageType="authorPage"
          />

          {totalItems > limit && (
            <Pagination pages={Math.ceil(totalItems / Math.max(limit, 1))} page={start + 1} maxSteps={5} />
          )}
        </>
      }
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug, params = [] } = context.params as { slug: string; params?: string[] };
  const pageNumberParam = params[0];

  if (Number(pageNumberParam) === 1) {
    return { redirect: { destination: `/vara-skribenter/${slug}`, permanent: true } };
  }

  const isPageNumberInvalid = pageNumberParam && (!Number(pageNumberParam) || Number(pageNumberParam) < 1);
  const hasMoreParams = params.length > 1;

  if (isPageNumberInvalid || hasMoreParams) {
    return { notFound: true };
  }

  const author = await getAuthorBySlug(slug);
  if (!author) {
    return { notFound: true };
  }

  const createName = (author?: { firstName?: string; lastName?: string }) => `${author?.firstName}%${author?.lastName}`;

  const alternativeAuthorNames = author.alternativeNames.map(createName).join(',');
  const startFrom = parseInt(pageNumberParam) > 0 ? parseInt(pageNumberParam) : 1;
  const searchResults = await fetchAuthorArticles(createName(author), alternativeAuthorNames, startFrom - 1);
  const maxPages = Math.ceil((searchResults?.totalItems ?? 1) / (searchResults?.limit ?? 1));
  if (pageNumberParam && Number(pageNumberParam) > maxPages) {
    return { notFound: true };
  }
  const adsData = await loadAdsData();

  const titleSuffix = await getLabradorConfig('miscsettings.site.siteTitleSuffix');
  const route = ['vara-skribenter', slug, pageNumberParam];

  return {
    props: withSerializationGuard({
      key: route.join('/'),
      author,
      searchResults,
      adsData,
      titleSuffix,
    }),
  };
};

export default PageAuthors;
